import { MarketplaceApi } from '@/lib/MarketplaceApi';
import { useState, useEffect } from 'react';
import { shopVar } from '@/lib/shopStyles';

interface WaitlistSignupProps {
    slug: string;
    email?: string;
}

export function WaitlistSignup({ slug, email }: WaitlistSignupProps) {
    const [waitlistEmail, setWaitlistEmail] = useState('');
    const [success, setSuccess] = useState(false);
    const [isOnWaitlist, setIsOnWaitlist] = useState(false);
    const [loading, setLoading] = useState(false);
    const [checkingWaitlist, setCheckingWaitlist] = useState(false);

    const marketplaceApi = new MarketplaceApi(import.meta.env.VITE_API_URL);

    useEffect(() => {
        if (email) {
            setWaitlistEmail(email);
            checkWaitlistStatus(email);
        }
    }, [email]);

    const checkWaitlistStatus = async (emailToCheck: string) => {
        setCheckingWaitlist(true);
        try {
            const result = await marketplaceApi.checkOnWaitlist(slug, emailToCheck);
            setIsOnWaitlist(result.success);
        } catch (error) {
            console.error('Failed to check waitlist status:', error);
        } finally {
            setCheckingWaitlist(false);
        }
    };

    const handleJoinWaitlist = async () => {
        setLoading(true);
        try {
            await marketplaceApi.joinWaitlist(slug, waitlistEmail);
            setWaitlistEmail('');
            setSuccess(true);
        } catch (error) {
            console.error('Failed to join waitlist:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleLeaveWaitlist = async () => {
        setLoading(true);
        try {
            await marketplaceApi.leaveWaitlist(slug, waitlistEmail);
            // Refresh the page after successfully leaving the waitlist
            window.location.reload();
        } catch (error) {
            console.error('Failed to leave waitlist:', error);
            setLoading(false);
        }
    };

    if (checkingWaitlist) {
        return (
            <div className='mt-4'>
                <p className='text-sm' style={{ color: shopVar('--shop-textColor') }}>
                    Checking waitlist status...
                </p>
            </div>
        );
    }

    if (success) {
        return (
            <p className='text-sm mt-2' style={{ color: shopVar('--shop-textColor') }}>
                {isOnWaitlist ? 'You have been removed from the waitlist' : 'You have been added to the waitlist'}
            </p>
        );
    }

    if (isOnWaitlist) {
        return (
            <>
                <div className='mt-4'>
                    <p className='text-sm mb-2' style={{ color: shopVar('--shop-textColor') }}>
                        You are currently on the waitlist with email: <strong>{waitlistEmail}</strong>
                    </p>
                    <button 
                        className='px-3 py-1.5 text-sm rounded border hover:cursor-pointer hover:opacity-80 flex items-center gap-1.5' 
                        style={{
                            borderColor: shopVar('--shop-borderColor'),
                            color: shopVar('--shop-textColor'),
                            backgroundColor: 'transparent',
                            opacity: loading ? 0.6 : 1
                        }}
                        onClick={loading ? undefined : handleLeaveWaitlist}
                        disabled={loading}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {loading ? 'Leaving...' : 'Leave waitlist'}
                    </button>
                </div>
            </>
        );
    }

    return (
        <>
            <div className='mt-4 flex gap-4'>
                <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    className="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
                    style={{
                        borderColor: shopVar('--shop-borderColor'),
                        color: shopVar('--shop-textColor'),
                        backgroundColor: shopVar('--shop-panelBackgroundColor'),
                    }}
                    onChange={(e) => {
                        setWaitlistEmail(e.target.value);
                    }}
                    value={waitlistEmail}
                    disabled={loading}
                />
                <div 
                    className='px-4 py-2 rounded-md grow text-nowrap hover:cursor-pointer hover:opacity-80 flex items-center gap-2' 
                    style={{
                        backgroundColor: shopVar('--shop-ctaButtonColor'),
                        color: shopVar('--shop-ctaButtonTextColor'),
                        opacity: loading || !waitlistEmail ? 0.6 : 1
                    }}
                    onClick={loading || !waitlistEmail ? undefined : handleJoinWaitlist}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 6V18M18 12H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {loading ? 'Joining...' : 'Join the waitlist'}
                </div>
            </div>
            <p className='text-sm mt-2' style={{ color: shopVar('--shop-textColor') }}>Get notified when tickets become available</p>
        </>
    );
} 